import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  Accordion,
  Alert,
  Box,
  Button,
  Container,
  Group,
  NumberInput,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import {
  ClientReferenceRow,
  getSelectionAnchors,
  KeyValueEditor,
  objectToPairs,
  pairsToObject,
  ReportViewer,
  type KeyValuePair,
} from '@/components';
import { CaseSharing } from '@/components/CaseSharing';
import { EntityChatAside } from '@/components/Chat';
import { MatchReviewPanel } from '@/components/MatchReview';
import {
  NpAssignmentAside,
  NpAssignmentsPanel,
  NpAssignmentsProvider,
} from '@/components/NpAssignments';
import { ProfileReviewPanel } from '@/components/ProfileReview';
import { RegistryOrdersCard } from '@/components/RegistryOrders';
import {
  ClientDocumentsPanel,
  FilePreview,
  ReportDocumentsPanel,
} from '@/components/ReportDocuments';
import { ReportFeedbackChatPanel } from '@/components/ReportFeedbackChat/ReportFeedbackChatPanel';
import { ReportJurisdictions } from '@/components/ReportJurisdictions';
import {
  groupDispatchedPartners,
  REGISTRY_PARTNER_SELECTION,
  registryPartnerOf,
  ReportPartnersPanel,
  reviewablePairs,
  ReviewModal,
  type DispatchedPartner,
} from '@/components/ReportPartners';
import { ScreeningOutcomePanel } from '@/components/ScreeningOutcome';
import { ReportCaseChain, RequestContextBar } from '@/components/TriageWorkspace';
import { useAuth } from '@/contexts/AuthContext';
import { useLayout, useLayoutOptional } from '@/contexts/LayoutContext';
import {
  ARMADILLO_ASIDE,
  armadilloReplyTargetOf,
  useArmadilloAsideLink,
} from '@/hooks/useArmadilloAsideLink';
import { useBackofficeAssignments } from '@/hooks/useBackofficeAssignments';
import { useBackofficeFiles } from '@/hooks/useBackofficeFiles';
import { usePoll } from '@/hooks/usePoll';
import { useReportPricing } from '@/hooks/useReportPricing';
import { useTextSelection } from '@/hooks/useTextSelection';
import { setPersistentAnchorHighlight } from '@/lib/anchorHighlight';
import { vnextApiClient } from '@/services/vnext-api';
import { transformToBackofficeSyncMarker } from '@/types/backoffice-sync';
import { registryDocumentsOrderable, registryDocumentsShown } from '@/types/registry-documents';
import type { BackofficeFile, ReportResponse } from '@/types/report';
import type { FeedbackAnchors } from '@/types/report-feedback';
import { currencySymbol, formatDueDate, formatMoney } from '@/utils';

/** Admin-only editor for a docx report's fill variables (plan 15 / B8).
 * Full-replace PATCH; values take effect on the next fill/re-run. */
function ReportFillVariables({
  reportId,
  reportMeta,
  onUpdated,
}: {
  reportId: string;
  reportMeta: ReportResponse;
  onUpdated: (updated: ReportResponse) => void;
}) {
  const [pairs, setPairs] = useState<KeyValuePair[]>(() =>
    objectToPairs(reportMeta.report_metadata)
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await vnextApiClient.updateReportMetadata(
        reportId,
        pairsToObject(pairs) ?? {}
      );
      onUpdated(updated);
      notifications.show({ color: 'green', message: 'Report fill variables saved' });
    } catch {
      notifications.show({ color: 'red', message: 'Failed to save fill variables' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion variant="contained" mb="md">
      <Accordion.Item value="fill-variables">
        <Accordion.Control>Report fill variables</Accordion.Control>
        <Accordion.Panel>
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Report-level values the docx fill merges over the config defaults key-by-key (matter
              reference, cover-page fields, …). Full replace; takes effect on the next fill.
            </Text>
            <KeyValueEditor
              pairs={pairs}
              onChange={setPairs}
              keyPlaceholder="matter_ref"
              valuePlaceholder="value"
              addLabel="Add variable"
            />
            <Group>
              <Button size="xs" onClick={handleSave} loading={saving}>
                Save
              </Button>
            </Group>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

const FEEDBACK_POLL_INTERVAL = 3000;
const FEEDBACK_POLL_MAX_DURATION = 120000; // 2 minutes

interface ReportAsideTabsProps {
  reportId: string;
  context: string | null;
  anchors: FeedbackAnchors | null;
  onClearContext: () => void;
  onFeedbackSubmitted: () => void;
  showFeedback: boolean;
  /** NPs locked on dispatched assignments — non-empty adds the admin-only
   * "Partners" tab (per-NP chat, same threads as triage). */
  partners: DispatchedPartner[];
  onPartnerReviewed?: () => void | Promise<void>;
  /** The `?aside=armadillo` link landed: open Partners on Armadillo. */
  landOnArmadillo?: boolean;
  /** What that link's `target` named: the email Armadillo's conversation
   * answers first. A new value opens the conversation again on it. */
  armadilloTarget?: string | null;
  /** True adds the admin-only "Documents" tab — every NP-submitted file
   * with previews, plus the "which docx is the report" anchor control. */
  showDocuments?: boolean;
  /** Parent request — adds the "Project documents" section to the tab. */
  reportRequestId?: string | null;
  /** Backoffice involvement — shows the "Assignment documents" section. */
  assignmentDocsEnabled?: boolean;
  /** Fetch the report's files even without Backoffice involvement — the
   * full lists a report's sections cap live there too. */
  documentFilesEnabled?: boolean;
  onDocumentAnchored?: () => void;
  /** Client-side shared documents — non-empty adds a client "Documents"
   * tab; selecting a file renders it on the main page. */
  clientFiles?: BackofficeFile[];
  clientDocFile?: BackofficeFile | null;
  onViewClientDoc?: (file: BackofficeFile | null) => void;
  /** True adds the admin-only "Match review" tab — the remediation matches
   * the config wants an analyst to adjudicate before synthesis. */
  showMatchReview?: boolean;
  /** Admin-only: the screening outcome — rating, subjects, retained
   * findings — as a tab of the aside. */
  showFindings?: boolean;
  onMatchReviewSubmitted?: () => void | Promise<void>;
  /** True adds the "Profile review" tab — the social media profiles a
   * reviewer confirms before a subject's screening is ordered. The gate is
   * the requesting team's as much as an admin's, so the client branch
   * carries the tab too. */
  showProfileReview?: boolean;
  onProfileReviewSubmitted?: () => void | Promise<void>;
}

/** The client aside's tab for the raw layout selection: one of the tabs
 * this client has, else Chat (an admin-only tab left in the layout state,
 * or one of its own that is not offered right now). */
function clientAsideTab(
  requested: string,
  shown: { documents: boolean; profileReview: boolean }
): string {
  if (requested === 'documents' && shown.documents) {
    return 'documents';
  }
  if (requested === 'profile-review' && shown.profileReview) {
    return 'profile-review';
  }
  return 'chat';
}

function ReportAsideTabs({
  reportId,
  context,
  anchors,
  onClearContext,
  onFeedbackSubmitted,
  showFeedback,
  partners,
  onPartnerReviewed,
  landOnArmadillo = false,
  armadilloTarget = null,
  showDocuments = false,
  reportRequestId = null,
  assignmentDocsEnabled = true,
  documentFilesEnabled = assignmentDocsEnabled,
  onDocumentAnchored,
  clientFiles = [],
  clientDocFile = null,
  onViewClientDoc,
  showMatchReview = false,
  showFindings = false,
  onMatchReviewSubmitted,
  showProfileReview = false,
  onProfileReviewSubmitted,
}: Readonly<ReportAsideTabsProps>) {
  const { asideTab, setAsideTab } = useLayout();
  const requestedTab = asideTab ?? 'chat';
  // Partners now lives INSIDE the Chat surface — a stale top-level
  // "partners" selection (persisted layout state from before the
  // consolidation) maps to chat. Stale "documents" falls back likewise.
  let tab = requestedTab === 'partners' ? 'chat' : requestedTab;
  if (tab === 'documents' && !showDocuments) {
    tab = 'chat';
  }
  if (tab === 'match-review' && !showMatchReview) {
    tab = 'chat';
  }
  if (tab === 'findings' && !showFindings) {
    tab = 'chat';
  }
  if (tab === 'profile-review' && !showProfileReview) {
    tab = 'chat';
  }

  // The partner cards + per-NP threads render as a tab of the chat
  // surface, next to the Client and Internal conversations.
  const partnersSection =
    partners.length > 0 ? (
      <ReportPartnersPanel
        reportId={reportId}
        partners={partners}
        onReviewed={onPartnerReviewed}
        initialNpId={landOnArmadillo ? REGISTRY_PARTNER_SELECTION : null}
        initialReplyTarget={armadilloTarget}
      />
    ) : undefined;

  if (!showFeedback) {
    // Clients: chat only — unless an admin shared documents with them
    // (their own Documents tab) or the report waits on their profile
    // confirmations (the Profile review tab). Derived from the RAW
    // selection: the admin fallback above maps 'documents' → 'chat'
    // whenever the ADMIN documents tab is hidden (showDocuments is
    // admin-only), which would eat every client click on their own
    // Documents tab.
    const showClientDocuments = clientFiles.length > 0;
    if (!showClientDocuments && !showProfileReview) {
      return <EntityChatAside entityType="report" entityId={reportId} />;
    }
    const clientTab = clientAsideTab(requestedTab, {
      documents: showClientDocuments,
      profileReview: showProfileReview,
    });
    return (
      <Tabs
        value={clientTab}
        onChange={(v) => setAsideTab(v)}
        keepMounted={false}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs.List>
          <Tabs.Tab value="chat">Chat</Tabs.Tab>
          {showClientDocuments && <Tabs.Tab value="documents">Documents</Tabs.Tab>}
          {showProfileReview && <Tabs.Tab value="profile-review">Profile review</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel
          value="chat"
          pt="md"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <EntityChatAside entityType="report" entityId={reportId} />
        </Tabs.Panel>
        {showClientDocuments && (
          <Tabs.Panel
            value="documents"
            pt="md"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <ClientDocumentsPanel
              files={clientFiles}
              viewing={clientDocFile}
              onView={(file) => onViewClientDoc?.(file)}
            />
          </Tabs.Panel>
        )}
        {showProfileReview && (
          <Tabs.Panel
            value="profile-review"
            pt="md"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <ProfileReviewPanel
              reportId={reportId}
              onSubmitted={onProfileReviewSubmitted}
              isAdmin={false}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    );
  }

  return (
    <Tabs
      value={tab}
      onChange={(v) => setAsideTab(v)}
      keepMounted={false}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Tabs.List>
        <Tabs.Tab value="chat">Chat</Tabs.Tab>
        {/* "AI QA" to match the Admin menu entry; the tab VALUE stays
            'feedback' — deep links and setAsideTab callers depend on it. */}
        <Tabs.Tab value="feedback">AI QA</Tabs.Tab>
        {showDocuments && <Tabs.Tab value="documents">Documents</Tabs.Tab>}
        {showMatchReview && <Tabs.Tab value="match-review">Match review</Tabs.Tab>}
        {showProfileReview && <Tabs.Tab value="profile-review">Profile review</Tabs.Tab>}
        {showFindings && <Tabs.Tab value="findings">Findings</Tabs.Tab>}
      </Tabs.List>
      <Tabs.Panel
        value="chat"
        pt="md"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <EntityChatAside
          entityType="report"
          entityId={reportId}
          partnersSection={partnersSection}
          initialTab={landOnArmadillo ? 'partners' : 'chat'}
          landingKey={armadilloTarget}
        />
      </Tabs.Panel>
      <Tabs.Panel
        value="feedback"
        pt="md"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <ReportFeedbackChatPanel
          reportId={reportId}
          context={context}
          anchors={anchors}
          onClearContext={onClearContext}
          onFeedbackSubmitted={onFeedbackSubmitted}
        />
      </Tabs.Panel>
      {showDocuments && (
        <Tabs.Panel
          value="documents"
          pt="md"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <ReportDocumentsPanel
            reportId={reportId}
            reportRequestId={reportRequestId}
            assignmentDocsEnabled={assignmentDocsEnabled}
            filesEnabled={documentFilesEnabled}
            onAnchored={onDocumentAnchored}
            partners={partners}
            onSentToNp={onPartnerReviewed}
          />
        </Tabs.Panel>
      )}
      {showMatchReview && (
        <Tabs.Panel
          value="match-review"
          pt="md"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <MatchReviewPanel reportId={reportId} onSubmitted={onMatchReviewSubmitted} />
        </Tabs.Panel>
      )}
      {showProfileReview && (
        <Tabs.Panel
          value="profile-review"
          pt="md"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          {/* This branch renders for admins only (showFeedback), so the
              panel may show the deployment setting. */}
          <ProfileReviewPanel reportId={reportId} onSubmitted={onProfileReviewSubmitted} isAdmin />
        </Tabs.Panel>
      )}
      {showFindings && (
        <Tabs.Panel
          value="findings"
          pt="md"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <ScreeningOutcomePanel reportId={reportId} />
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

/**
 * Client-facing commercials — price and TAT/due date the client agreed
 * to, pulled from the report's effective fields. Admins get the fuller
 * RequestContextBar instead; this strip renders nothing when no fact
 * is known (e.g. legacy self-served rows with no config pricing).
 */
function ClientCommercialsStrip({ meta }: Readonly<{ meta: ReportResponse }>) {
  const facts: string[] = [];
  const money = formatMoney(meta.price ?? null, meta.currency);
  if (money) {
    facts.push(money);
  }
  if (meta.due_at) {
    facts.push(`Due ${formatDueDate(meta.due_at)}`);
  } else if (meta.turnaround_time_days != null) {
    facts.push(`${meta.turnaround_time_days}d turnaround`);
  }
  if (facts.length === 0) {
    return null;
  }
  return (
    <Group gap={6} mb="md" wrap="wrap" data-testid="client-commercials">
      {facts.map((fact, i) => (
        <Group key={fact} gap={6} wrap="nowrap">
          {i > 0 && (
            <Text size="sm" c="dimmed">
              ·
            </Text>
          )}
          <Text size="sm" c="dimmed">
            {fact}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

/** The stage of `reportId`, or null until its own metadata has loaded —
 * `reportMeta` is kept across a change of report and may be the last one's. */
function stageOf(meta: ReportResponse | null, reportId: string): ReportResponse['status'] | null {
  return meta?.id === reportId ? meta.status : null;
}

/** The config name of `reportId`, or null until its own metadata has
 * loaded. The viewer's cover shows it while the viewer's own fetch holds
 * no report: a stage parked on a person (the profile review gate) or in
 * flight, where the cover would otherwise fall back to its default level. */
function configNameOf(meta: ReportResponse | null, reportId: string): string | null {
  return meta?.id === reportId ? meta.config_name : null;
}

/**
 * The report's metadata, read once per report, and which report's read has
 * come back (loaded or failed): the registry-documents gate waits for it
 * instead of mounting a card the metadata may then take away. A read that
 * fails leaves the metadata as it was, as it always has.
 */
function useReportMeta(reportId: string | undefined) {
  const [reportMeta, setReportMeta] = useState<ReportResponse | null>(null);
  const [metaSettledFor, setMetaSettledFor] = useState<string | null>(null);
  useEffect(() => {
    if (!reportId) {
      return;
    }
    let cancelled = false;
    vnextApiClient
      .getReport(reportId)
      .then(setReportMeta)
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setMetaSettledFor(reportId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);
  return { reportMeta, setReportMeta, metaSettledFor };
}

/** Whether the registry-documents card belongs on `reportId` (`shown`) and
 * whether it offers new orders there (`canOrder`). Hidden until the
 * report's own metadata fetch has settled, then only its own metadata
 * saying no hides it: a failed fetch, or metadata that is not this
 * report's (same caveat as `stageOf`), reads as available, the same
 * default as an API without the field. A report with no live assignment
 * locked to Armadillo still shows the card for the orders it already has,
 * without the way to order more; the metadata is re-read when a lock
 * changes, so the card follows. */
function registryDocumentsCard(
  meta: ReportResponse | null,
  reportId: string,
  settled: boolean
): { shown: boolean; canOrder: boolean } {
  if (!settled) {
    return { shown: false, canOrder: false };
  }
  const scope = meta?.id === reportId ? meta.registry_documents : undefined;
  return { shown: registryDocumentsShown(scope), canOrder: registryDocumentsOrderable(scope) };
}

export function ReportPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('print') === 'true';

  const { user } = useAuth();
  const isAdmin = !!user?.isAdministrator;

  const layout = useLayoutOptional();
  const registerAside = layout?.registerAside;
  const setAsideOpen = layout?.setAsideOpen;
  const setAsideTab = layout?.setAsideTab;

  const [feedbackContext, setFeedbackContext] = useState<string | null>(null);
  const [feedbackAnchors, setFeedbackAnchors] = useState<FeedbackAnchors | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { reportMeta, setReportMeta, metaSettledFor } = useReportMeta(reportId);
  const reportContentRef = useRef<HTMLDivElement>(null);

  const needsNpDispatch =
    isAdmin && reportMeta?.trigger === 'escalation' && !reportMeta.backoffice_project_id;

  // Jurisdictions become Backoffice tags once the project exists; until then
  // they are edited in triage, so there is nothing to offer here.
  const showJurisdictions = !!reportMeta?.backoffice_project_id && !needsNpDispatch;

  const needsFileSelection = isAdmin && reportMeta?.status === 'awaiting_file_selection';

  // The match-review gate: remediation queued matches the config wants an
  // analyst to adjudicate. The tab stays available to admins on every
  // non-print view so applied verdicts remain inspectable afterwards.
  const needsMatchReview = isAdmin && reportMeta?.status === 'awaiting_match_review';
  const showMatchReviewTab = isAdmin && !isPrintMode;

  const refreshReportMeta = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      setReportMeta(await vnextApiClient.getReport(reportId));
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [reportId]);

  // The profile-review gate: social media profiles the system could not
  // vouch for wait on a reviewer before the screening is ordered. The
  // reviewer is an admin or anyone on the requesting team, so the gate
  // reads the stage alone. Admins keep the tab on every non-print view
  // until the report is completed (the panel shows its own empty state);
  // the requesting team gets it only while the report waits on them.
  const needsProfileReview = reportMeta?.status === 'awaiting_profile_review';
  const showProfileReviewTab =
    !isPrintMode && (needsProfileReview || (isAdmin && reportMeta?.status !== 'completed'));

  // Submitting the decisions moves the report off the gate: refetch the
  // meta so the alert clears, and remount the viewer so it re-fetches.
  // At the gate the viewer's hook holds without polling (a parked
  // stage), so nothing else would pick the next stage up.
  const handleProfileReviewSubmitted = useCallback(async () => {
    setRefreshKey((k) => k + 1);
    await refreshReportMeta();
  }, [refreshReportMeta]);

  // Assignment documents apply only to reports with Backoffice
  // involvement, at the stages the backend lists files for.
  // backoffice_project_id covers both the legacy triage flow and
  // escalation dispatches.
  const assignmentDocsEnabled =
    !!reportMeta?.backoffice_project_id &&
    ['awaiting_fulfilment', 'awaiting_file_selection', 'in_qa', 'completed'].includes(
      reportMeta?.status ?? ''
    );
  // The Documents tab also carries the parent request's attached source
  // documents ("Project documents"), so any report with a parent request
  // gets the tab.
  // The full lists behind a section's display caps are listed once the
  // report is in QA or done, with or without Backoffice involvement.
  const documentFilesEnabled =
    assignmentDocsEnabled || ['in_qa', 'completed'].includes(reportMeta?.status ?? '');
  const showDocumentsTab =
    isAdmin &&
    !isPrintMode &&
    (assignmentDocsEnabled || !!reportMeta?.report_request_id || documentFilesEnabled);

  // Client-side shared documents: fetched only for completed reports the
  // client can reach — the backend returns [] until an admin shares
  // something AND the report is client-visible (approval rules).
  const clientDocumentsEnabled = !isAdmin && !isPrintMode && reportMeta?.status === 'completed';
  const { files: clientFiles } = useBackofficeFiles(
    clientDocumentsEnabled && reportId ? reportId : null
  );
  const [clientDocFile, setClientDocFile] = useState<BackofficeFile | null>(null);
  useEffect(() => {
    // A viewed file that got unshared (or a refetch dropping it) falls
    // back to the report rather than a dead preview.
    if (clientDocFile && !clientFiles.some((f) => f.raw_data_key === clientDocFile.raw_data_key)) {
      setClientDocFile(null);
    }
  }, [clientFiles, clientDocFile]);

  // Admin-only: dispatched assignments feed the aside's "Partners" tab.
  // Skipped while the escalation-dispatch aside owns the assignment data
  // (the flip to false after dispatch triggers a fresh fetch here).
  const { updatePrice } = useReportPricing();
  const { data: assignmentsData, refetch: refetchAssignments } = useBackofficeAssignments(
    isAdmin && !isPrintMode && !needsNpDispatch && reportId ? reportId : null
  );
  const partners = useMemo(
    () => groupDispatchedPartners(assignmentsData?.assignments ?? []),
    [assignmentsData]
  );

  // Approve-time rating chain: approving flips this on, and the modal
  // walks the completed-but-unreviewed engagements one by one (each
  // review refetches → the next pair surfaces). Dismissing stops it —
  // the Partners tab keeps its "Rate this partner" button as the
  // non-nagging fallback.
  const [ratingPromptActive, setRatingPromptActive] = useState(false);
  const ratingTarget = useMemo(
    () => (ratingPromptActive ? (reviewablePairs(partners)[0] ?? null) : null),
    [ratingPromptActive, partners]
  );
  const handleApproved = useCallback(() => {
    setRatingPromptActive(true);
  }, []);
  useEffect(() => {
    // Chain drained (or nothing to rate) — drop the flag so a later
    // data refresh can't resurface the modal out of nowhere.
    if (ratingPromptActive && reviewablePairs(partners).length === 0) {
      setRatingPromptActive(false);
    }
  }, [ratingPromptActive, partners]);

  // Content-change watch after feedback: each submission opens a new
  // session (restarting the two-minute clock); the poll ends itself the
  // moment a change lands.
  const [contentPollSession, setContentPollSession] = useState(0);
  const contentSnapshotRef = useRef<string | null>(null);
  const versionSnapshotRef = useRef<number | null>(null);

  const isDocxReport = Boolean(reportMeta?.is_docx);

  usePoll<{ changed: boolean }>({
    enabled: contentPollSession > 0 && reportId !== undefined,
    interval: FEEDBACK_POLL_INTERVAL,
    // The edit was just submitted — the first look waits one interval.
    immediate: false,
    maxDurationMs: FEEDBACK_POLL_MAX_DURATION,
    refetchDeps: [contentPollSession],
    queryFn: async ({ signal }) => {
      if (!reportId) {
        throw new Error('reportId is required');
      }
      if (isDocxReport) {
        const versions = await vnextApiClient.getReportContentVersions(reportId, { signal });
        const maxVersion = versions.reduce((max, v) => Math.max(max, v.version), 0);
        return {
          changed: versionSnapshotRef.current !== null && maxVersion > versionSnapshotRef.current,
        };
      }
      const report = await vnextApiClient.getReport(reportId, { signal });
      return { changed: Boolean(report.content) && report.content !== contentSnapshotRef.current };
    },
    until: (tick) => tick.changed,
    onData: (tick) => {
      if (tick.changed) {
        setRefreshKey((k) => k + 1);
      }
    },
    onStop: () => {
      contentSnapshotRef.current = null;
      versionSnapshotRef.current = null;
    },
    onError: (err) => Sentry.captureException(err),
  });

  const startContentPolling = useCallback(() => {
    if (!reportId) {
      return;
    }
    contentSnapshotRef.current = null;
    versionSnapshotRef.current = null;

    if (isDocxReport) {
      // Docx reports render client-side — `report.content` is always null,
      // so the content-diff poll below can never fire. The durable signal
      // for a completed document-edit is a NEW ReportContent version row:
      // snapshot the current max version and watch for a higher one.
      vnextApiClient
        .getReportContentVersions(reportId)
        .then((versions) => {
          versionSnapshotRef.current = versions.reduce((max, v) => Math.max(max, v.version), 0);
        })
        .catch(() => {
          versionSnapshotRef.current = 0;
        });
    } else {
      vnextApiClient
        .getReport(reportId)
        .then((report) => {
          contentSnapshotRef.current = report.content;
        })
        .catch(() => {
          // If we can't snapshot, still try polling
        });
    }

    setContentPollSession((s) => s + 1);
  }, [reportId, isDocxReport]);

  // Inbox deep links land with ?aside=documents so the analyst arrives
  // straight in the Documents tab (file selection / resubmission review).
  // One-shot (ref-guarded): later context identity changes must not yank
  // the analyst back after they switch tabs. The selection survives while
  // the tab is still gated — it renders once reportMeta confirms it.
  const asideDeepLinkConsumed = useRef(false);
  useEffect(() => {
    if (asideDeepLinkConsumed.current) {
      return;
    }
    const aside = searchParams.get('aside');
    const adminTab = aside === 'documents' || aside === 'match-review';
    // Profile review is the requesting team's gate too (the request page
    // links here), so that link is honoured for every caller.
    const linked = aside === 'profile-review' || (adminTab && isAdmin);
    if (linked && !isPrintMode) {
      asideDeepLinkConsumed.current = true;
      setAsideTab?.(aside);
      setAsideOpen?.(true);
    }
  }, [searchParams, isAdmin, isPrintMode, setAsideTab, setAsideOpen]);

  // ?aside=armadillo (the Slack ping, the registry inbox items): Chat →
  // Partners → Armadillo, once the assignments say Armadillo is listed.
  const armadillo = useArmadilloAsideLink({
    requested: searchParams.get('aside') === ARMADILLO_ASIDE,
    target: armadilloReplyTargetOf(searchParams),
    enabled: isAdmin && !isPrintMode,
    assignmentsLoaded: assignmentsData != null,
    hasRegistryPartner: registryPartnerOf(partners) !== null,
    setAsideTab,
    setAsideOpen,
  });

  const handleOpenFeedback = useCallback(() => {
    setAsideTab?.('feedback');
    setAsideOpen?.(true);
  }, [setAsideTab, setAsideOpen]);

  const handleSelectionFeedback = useCallback(
    (selectedText: string) => {
      setFeedbackContext(selectedText);
      // The selection is still live at this point (the feedback button
      // preventDefaults its mousedown) — resolve it to paraId anchors so
      // docx feedback lands on exact blocks. Null outside the Content view.
      const anchors = getSelectionAnchors();
      setFeedbackAnchors(anchors);
      // Keep the selection visibly marked in the document while the
      // analyst composes (the browser selection dies on the first click).
      setPersistentAnchorHighlight(anchors?.para_ids ?? []);
      handleOpenFeedback();
    },
    [handleOpenFeedback]
  );

  // Selection-based feedback affordance is admin-only.
  useTextSelection(reportContentRef, handleSelectionFeedback, { enabled: isAdmin });

  const handleClearContext = useCallback(() => {
    setFeedbackContext(null);
    setFeedbackAnchors(null);
    setPersistentAnchorHighlight([]);
  }, []);

  // Leaving the page must not strand a compose-time marker.
  useEffect(() => {
    return () => setPersistentAnchorHighlight([]);
  }, []);

  const handleFeedbackSubmitted = useCallback(() => {
    startContentPolling();
  }, [startContentPolling]);

  // Anchoring a docx changed the report content (and, on the first-time
  // selection, completes the report) — remount the viewer for the fresh
  // bytes and refetch the meta so the file-selection alert clears. Adding
  // a registry digest to the report's docx is the same event.
  const handleDocumentAnchored = useCallback(() => {
    setRefreshKey((k) => k + 1);
    if (reportId) {
      vnextApiClient
        .getReport(reportId)
        .then(setReportMeta)
        .catch(() => null);
    }
  }, [reportId]);

  useEffect(() => {
    if (!reportId || isPrintMode || !registerAside) {
      return;
    }
    registerAside({
      title: needsNpDispatch ? 'NP Assignments' : 'Conversation',
      content: needsNpDispatch ? (
        <NpAssignmentsProvider
          reportId={reportId}
          reportRequestId={null}
          onLockChanged={refreshReportMeta}
        >
          <NpAssignmentAside />
        </NpAssignmentsProvider>
      ) : (
        <ReportAsideTabs
          reportId={reportId}
          context={feedbackContext}
          anchors={feedbackAnchors}
          onClearContext={handleClearContext}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          showFeedback={isAdmin}
          partners={partners}
          onPartnerReviewed={refetchAssignments}
          landOnArmadillo={armadillo.landed}
          armadilloTarget={armadillo.target}
          showDocuments={showDocumentsTab}
          reportRequestId={reportMeta?.report_request_id ?? null}
          assignmentDocsEnabled={assignmentDocsEnabled}
          documentFilesEnabled={documentFilesEnabled}
          onDocumentAnchored={handleDocumentAnchored}
          clientFiles={clientFiles}
          clientDocFile={clientDocFile}
          onViewClientDoc={setClientDocFile}
          showMatchReview={showMatchReviewTab}
          showFindings={showMatchReviewTab}
          onMatchReviewSubmitted={refreshReportMeta}
          showProfileReview={showProfileReviewTab}
          onProfileReviewSubmitted={handleProfileReviewSubmitted}
        />
      ),
    });
    return () => {
      registerAside(null);
    };
  }, [
    reportId,
    isPrintMode,
    needsNpDispatch,
    feedbackContext,
    feedbackAnchors,
    handleClearContext,
    handleFeedbackSubmitted,
    isAdmin,
    partners,
    refetchAssignments,
    armadillo,
    registerAside,
    showDocumentsTab,
    assignmentDocsEnabled,
    documentFilesEnabled,
    reportMeta?.report_request_id,
    handleDocumentAnchored,
    clientFiles,
    clientDocFile,
    showMatchReviewTab,
    refreshReportMeta,
    showProfileReviewTab,
    handleProfileReviewSubmitted,
  ]);

  if (!reportId) {
    return (
      <Container size="xl" py="md">
        <Text c="red">No report ID provided.</Text>
      </Container>
    );
  }

  if (isPrintMode) {
    return <ReportViewer reportId={reportId} printMode />;
  }

  const registryCard = registryDocumentsCard(reportMeta, reportId, metaSettledFor === reportId);

  return (
    <Container size="xl" py="md" ref={reportContentRef}>
      {/* Admin-only: the case chain — one hop back to the request /
          inbound enquiry this report belongs to. */}
      {isAdmin && reportId && (
        <Stack mb="md" gap="md">
          <ReportCaseChain reportId={reportId} />
          {/* Registry documents ordered from Armadillo for this report, where
              Armadillo i-doc is locked on a live partner assignment or the
              report already has orders (then without new ones). One line
              when there are none - most such reports order nothing. Keyed by
              report: this page outlives a change of reportId. */}
          {registryCard.shown && (
            <RegistryOrdersCard
              key={reportId}
              reportId={reportId}
              subjectSource={{ reportId }}
              reportStage={stageOf(reportMeta, reportId)}
              canOrder={registryCard.canOrder}
              compactWhenEmpty
              onReportContentChanged={handleDocumentAnchored}
            />
          )}
        </Stack>
      )}
      {isAdmin && reportId && reportMeta?.content_kind === 'docx' && (
        <ReportFillVariables
          reportId={reportId}
          reportMeta={reportMeta}
          onUpdated={(updated) =>
            setReportMeta((prev) =>
              prev ? { ...prev, report_metadata: updated.report_metadata } : prev
            )
          }
        />
      )}
      {isAdmin && !isPrintMode && reportId && showJurisdictions && (
        <Stack mb="md" gap={0}>
          <ReportJurisdictions
            reportId={reportId}
            jurisdictions={reportMeta?.jurisdictions ?? []}
            syncMarker={transformToBackofficeSyncMarker(
              reportMeta?.backoffice_sync_error,
              reportMeta?.backoffice_sync_failed_at
            )}
            onUpdated={(updated) =>
              setReportMeta((prev) =>
                prev
                  ? {
                      ...prev,
                      jurisdictions: updated.jurisdictions,
                      backoffice_sync_error: updated.backoffice_sync_error,
                      backoffice_sync_failed_at: updated.backoffice_sync_failed_at,
                    }
                  : prev
              )
            }
          />
        </Stack>
      )}
      {needsFileSelection && (
        <Alert color="blue" title="File selection required" mb="md" withCloseButton={false}>
          <Stack gap="xs">
            <Text size="sm">
              All backoffice assignments are complete. Pick the submitted docx that becomes the
              report in the Documents tab.
            </Text>
            <Button
              size="sm"
              onClick={() => {
                setAsideTab?.('documents');
                setAsideOpen?.(true);
              }}
              style={{ alignSelf: 'flex-start' }}
              data-testid="open-documents-tab"
            >
              Open Documents
            </Button>
          </Stack>
        </Alert>
      )}
      {needsMatchReview && (
        <Alert color="grape" title="Screening matches need review" mb="md" withCloseButton={false}>
          <Stack gap="xs">
            <Text size="sm">
              Remediation found matches this report&apos;s configuration wants an analyst to confirm
              or reject before the report is written. Decide each one in the Match review tab and
              submit.
            </Text>
            <Button
              size="sm"
              onClick={() => {
                setAsideTab?.('match-review');
                setAsideOpen?.(true);
              }}
              style={{ alignSelf: 'flex-start' }}
              data-testid="open-match-review-tab"
            >
              Review matches
            </Button>
          </Stack>
        </Alert>
      )}
      {needsProfileReview && (
        <Alert color="orange" title="Profiles need confirming" mb="md" withCloseButton={false}>
          <Stack gap="xs">
            <Text size="sm">
              Social media profiles need confirming before screening can continue. Confirm or reject
              each one in the Profile review tab and submit.
            </Text>
            <Button
              size="sm"
              onClick={() => {
                setAsideTab?.('profile-review');
                setAsideOpen?.(true);
              }}
              style={{ alignSelf: 'flex-start' }}
              data-testid="open-profile-review-tab"
            >
              Open profile review
            </Button>
          </Stack>
        </Alert>
      )}
      {needsNpDispatch && (
        <Stack gap="md" mb="md">
          <NumberInput
            label="Client price"
            description="Client-facing report price (optional — can be set after dispatch)"
            placeholder="0"
            min={0}
            allowDecimal
            decimalScale={2}
            prefix={currencySymbol(reportMeta?.currency)}
            defaultValue={
              reportMeta?.price_override != null ? Number(reportMeta.price_override) : undefined
            }
            onBlur={(e) => {
              const raw = e.currentTarget.value.trim();
              const parsed = raw === '' ? null : Number(raw);
              void updatePrice(reportId, parsed).then((updated) => {
                if (updated) {
                  setReportMeta((prev) =>
                    prev ? { ...prev, price_override: updated.price_override } : prev
                  );
                }
              });
            }}
          />
          {/* A lock changed here moves the registry-documents gate the
              metadata carries: it is re-read so the card above follows. */}
          <NpAssignmentsProvider
            reportId={reportId}
            reportRequestId={null}
            onLockChanged={refreshReportMeta}
          >
            <NpAssignmentsPanel
              submitLabel="Dispatch to Backoffice"
              onAdvance={async () => {
                await vnextApiClient.dispatchBackofficeAssignments(reportId);
                setReportMeta((prev) =>
                  prev ? { ...prev, backoffice_project_id: 'dispatched' } : prev
                );
              }}
            />
          </NpAssignmentsProvider>
        </Stack>
      )}
      {isAdmin && reportMeta?.report_request_id && (
        <Stack mb="md" gap={0}>
          {/* Request context — commercials facts, files, Backoffice link —
              horizontal so it never costs reading width. */}
          <RequestContextBar
            reportRequestId={reportMeta.report_request_id}
            backofficeProjectId={reportMeta.backoffice_project_id}
            dueAt={reportMeta.due_at}
          />
        </Stack>
      )}
      {!isAdmin && reportMeta?.report_request_id && (
        <Group mb="xs">
          <ClientReferenceRow
            value={reportMeta.client_reference ?? null}
            onSave={async (value) => {
              const requestId = reportMeta.report_request_id;
              if (!requestId) {
                return;
              }
              await vnextApiClient.updateClientReportRequest(requestId, {
                client_reference: value,
              });
              setReportMeta((prev) => (prev ? { ...prev, client_reference: value } : prev));
            }}
          />
        </Group>
      )}
      {!isAdmin && reportMeta && <ClientCommercialsStrip meta={reportMeta} />}
      {/* Sharing rides the parent request when there is one (the whole
          codename family follows); self-served reports — no parent RR —
          anchor the share on the report itself. Shown to clients and
          admins alike; the API gates who can modify. */}
      {reportMeta && reportId && (
        <Stack mb="md" gap={0}>
          <CaseSharing
            anchor={
              reportMeta.report_request_id
                ? { kind: 'request', id: reportMeta.report_request_id }
                : { kind: 'report', id: reportId }
            }
          />
        </Stack>
      )}
      {!isAdmin && clientDocFile ? (
        // A shared document takes over the main area — same preview stack
        // as the admin dialog, full-page. The aside (and this header)
        // navigate back to the report.
        <Stack gap="sm" data-testid="client-document-view">
          <Group justify="space-between" wrap="nowrap">
            <Title order={4} style={{ minWidth: 0 }} lineClamp={1}>
              {clientDocFile.filename}
            </Title>
            <Button
              variant="default"
              size="xs"
              onClick={() => setClientDocFile(null)}
              style={{ flexShrink: 0 }}
              data-testid="client-document-back"
            >
              Back to the report
            </Button>
          </Group>
          <Box style={{ height: '80vh' }}>
            <FilePreview file={clientDocFile} reportId={reportId} />
          </Box>
        </Stack>
      ) : (
        <ReportViewer
          key={refreshKey}
          reportId={reportId}
          configName={configNameOf(reportMeta, reportId)}
          onOpenFeedback={isAdmin ? handleOpenFeedback : undefined}
          onApproved={isAdmin ? handleApproved : undefined}
        />
      )}
      <ReviewModal
        target={ratingTarget}
        onClose={() => setRatingPromptActive(false)}
        onReviewed={refetchAssignments}
      />
    </Container>
  );
}
